// bridge-fetch.mjs

let msgId = 0;
const pendingFetches = new Map();
let initComplete = false;

/**
 * Convert base64 to ArrayBuffer
 * @param {string} base64
 * @returns {ArrayBuffer}
 */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Setup postMessage listener once per session
 */
function initBridgeListenerOnce() {
  if (initComplete) {
    return;
  }
  initComplete = true;

  window.addEventListener('message', (event) => {
    const data = event.data;
    if (data?.type !== 'FETCH_RESPONSE' || !data.id) return;

    const callbacks = pendingFetches.get(data.id);
    if (!callbacks) return;

    const { resolve, reject } = callbacks;
    pendingFetches.delete(data.id);

    if (data.error) {
      reject(new TypeError(data.message || 'Network request failed'));
      return;
    }

    const headers = new Headers(data.headers || {});
    const contentType = headers.get('content-type') || 'application/octet-stream';
    const buffer = base64ToArrayBuffer(data.bodyBase64);
    const blob = new Blob([buffer], { type: contentType });

    const response = new Response(blob, {
      status: data.status,
      statusText: data.statusText,
      headers,
    });

    resolve(response);
  });
}

/**
 * Fetch replacement for WebView that routes to React Native
 * @param {RequestInfo} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
function fetchViaBridge(url, options = {}) {
  if (!window.ReactNativeWebView?.postMessage || !(window.APP_VERSION > 1)) {
    return fetch(url, options);
  }

  initBridgeListenerOnce();

  const id = ++msgId;
  const payload = {
    type: 'FETCH_REQUEST',
    id,
    url: url.startsWith('/') ? (window.APP_HOST ?? '') + url : url,
    options,
  };

  return new Promise((resolve, reject) => {
    pendingFetches.set(id, { resolve, reject });
    window.ReactNativeWebView.postMessage(JSON.stringify(payload));
  });
}

export { fetchViaBridge as fetch };